import { shouldRedirectSignedInUserToPreferences, shouldResumeLinkedAccountSetup } from "./account-linking.js";
import {
  buildExistingPreferencesPath,
  buildOnboardingPreferencesPath,
  EXISTING_PREFERENCES_PATH,
} from "./preferences-route.js";

export const PREFERENCES_RESUME_KEY = "mincool.preferencesResume";
export const PREFERENCES_RESUME_VALUE = "preferences";

function canUseWindow() {
  return typeof window !== "undefined";
}

function getStorageTargets() {
  if (!canUseWindow()) {
    return [];
  }
  return [window.localStorage, window.sessionStorage].filter(Boolean);
}

function normalizeResumeValue(value = "") {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === PREFERENCES_RESUME_VALUE ? normalized : "";
}

function buildSearchParams(search = "") {
  return new URLSearchParams(String(search || "").replace(/^\?/, ""));
}

export function isPreferencesResumeValue(value = "") {
  return normalizeResumeValue(value) === PREFERENCES_RESUME_VALUE;
}

export function readPreferencesResumeTargetFromSearch(search = "") {
  return normalizeResumeValue(buildSearchParams(search).get("resume"));
}

export function withPreferencesResume(path = "/") {
  const url = new URL(String(path || "/"), "https://min.cool");
  url.searchParams.set("resume", PREFERENCES_RESUME_VALUE);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getPreferencesSignInPath() {
  return withPreferencesResume("/auth");
}

export function getPreferencesLinkPath() {
  return withPreferencesResume("/link");
}

export function readStoredPreferencesResumeTarget() {
  for (const store of getStorageTargets()) {
    const value = normalizeResumeValue(store.getItem(PREFERENCES_RESUME_KEY));
    if (value) {
      return value;
    }
  }
  return "";
}

export function rememberPreferencesResumeTarget(value = PREFERENCES_RESUME_VALUE) {
  const normalizedValue = normalizeResumeValue(value);
  if (!normalizedValue) {
    return "";
  }
  for (const store of getStorageTargets()) {
    store.setItem(PREFERENCES_RESUME_KEY, normalizedValue);
  }
  return normalizedValue;
}

export function clearPreferencesResumeTarget() {
  for (const store of getStorageTargets()) {
    store.removeItem(PREFERENCES_RESUME_KEY);
  }
}

export function syncPreferencesResumeTarget(search = "") {
  const searchValue = readPreferencesResumeTargetFromSearch(search);
  if (searchValue) {
    return rememberPreferencesResumeTarget(searchValue);
  }
  return readStoredPreferencesResumeTarget();
}

export function shouldResumeToPreferences(search = "") {
  return isPreferencesResumeValue(readPreferencesResumeTargetFromSearch(search))
    || isPreferencesResumeValue(readStoredPreferencesResumeTarget());
}

export function resolvePreferencesEntryPath({
  hasAuthenticatedUser = false,
  accountPayload = null,
  forceLiveExistingPreferences = false,
  accountId = "",
} = {}) {
  if (!hasAuthenticatedUser) {
    return getPreferencesSignInPath();
  }
  const destination = shouldRedirectSignedInUserToPreferences(accountPayload)
    ? "preferences"
    : shouldResumeLinkedAccountSetup(accountPayload) ? "setup" : "link";
  return resolvePreferencesDestinationPath(
    destination,
    { forceLiveExistingPreferences, accountId },
  );
}

export function resolvePreferencesAuthPath(
  userMode = "",
  { forceLiveExistingPreferences = false, accountId = "" } = {},
) {
  return String(userMode || "").trim().toLowerCase() === "new"
    ? getPreferencesLinkPath()
    : buildExistingPreferencesPath({
      forceLive: forceLiveExistingPreferences,
      accountId,
    });
}

export function resolvePostLinkPreferencesPath({
  search = "",
  hasAuthenticatedUser = false,
  forceLiveExistingPreferences = false,
  accountId = "",
} = {}) {
  if (!hasAuthenticatedUser || !isPreferencesResumeValue(readPreferencesResumeTargetFromSearch(search))) {
    return "";
  }
  return buildExistingPreferencesPath({
    forceLive: forceLiveExistingPreferences,
    accountId,
  });
}

export function resolvePreferencesDestinationPath(
  destination = "",
  { forceLiveExistingPreferences = false, accountId = "" } = {},
) {
  const normalizedDestination = String(destination || "").trim().toLowerCase();
  if (normalizedDestination === "setup") {
    return buildOnboardingPreferencesPath({
      forceLive: forceLiveExistingPreferences,
      accountId,
    });
  }
  return normalizedDestination === "preferences"
    ? buildExistingPreferencesPath({
      forceLive: forceLiveExistingPreferences,
      accountId,
    })
    : getPreferencesLinkPath();
}
